/**
 * Default letter templates. User can copy-paste content and use dynamics in square brackets, e.g. [Candidate_Name].
 * The system extracts [Placeholder_Name] and fills them from required sources (form, company, etc.).
 */

export const DEFAULT_OFFER_LETTER_TEMPLATE = {
  name: "Offer Letter (Default)",
  type: "offer",
  content: `<div class="letter-preview-content">
  <div class="letter-recipient-block">
    <p><strong>[Candidate_Name]</strong></p>
    <p>[Parent_Guardian]</p>
    <p>[Address_Line1]</p>
    <p>[Address_Line2]</p>
    <p>Mobile No. [Mobile]</p>
    <p>[Email]</p>
  </div>
  <p class="letter-subject">Subject: [Letter_Type]</p>
  <p class="letter-salutation">Dear [Candidate_Name],</p>
  <p>With reference to our discussion, we are pleased to offer you the job of <strong>"[Job_Title]"</strong> as per employment policy of the company. Your job role will be based at a project site decided as per company requirement.</p>
  <p>You are required to join on <strong>[Joining_Date]</strong>. Kindly report initially at <u>[Initial_Reporting_Location]</u> to join the services and confirm by mail of your joining for further process.</p>
  <p>Your CTC will be as has been discussed and agreed upon with <u>[Company_Management]</u>, during your visit to our <u>[Office_Location]</u> office.</p>
  <p><strong>Note:</strong> You shall report to Mr. <strong><u>[Reporting_Manager_Name] ([Reporting_Manager_Designation])</u></strong> for any location / communication please reach up to <strong><u>[Alternative_Contact_Name] ([Alternative_Contact_Phone])</u></strong></p>
  <p>Please accept this mail as a token of acceptance.</p>
  <p>We welcome you to team [Company_Team_Name]!</p>
  <div class="letter-closing letter-sender-block">
    <p>Thanks,</p>
    <p><strong>[Sender_Name]</strong></p>
    <p>[Sender_Designation]</p>
    <p>Mob. No. [Sender_Mobile]</p>
    <p>[Company_Legal_Name]</p>
    <p>Website: <u>[Company_Website]</u></p>
  </div>
</div>`,
};

export const DEFAULT_APPOINTMENT_LETTER_TEMPLATE = {
  name: "Appointment Letter (Default)",
  type: "appointment",
  content: `<div class="letter-preview-content">
  <div class="letter-recipient-block">
    <p><strong>[Candidate_Name]</strong></p>
    <p>[Parent_Guardian]</p>
    <p>[Address_Line1]</p>
    <p>[Address_Line2]</p>
    <p>Mobile No. [Mobile]</p>
    <p>[Email]</p>
  </div>
  <p class="letter-subject">Subject: [Letter_Type]</p>
  <p class="letter-salutation">Dear [Candidate_Name],</p>
  <p>With reference to our discussion, we are pleased to confirm your appointment as <strong>"[Job_Title]"</strong> in the <strong>[Department]</strong> department as per employment policy of the company.</p>
  <p>Your date of joining is <strong>[Joining_Date]</strong>. Kindly report initially at <u>[Initial_Reporting_Location]</u> to join the services and confirm by mail of your joining for further process.</p>
  <p>Your CTC will be as has been discussed and agreed upon with <u>[Company_Management]</u>, during your visit to our <u>[Office_Location]</u> office.</p>
  <p><strong>Note:</strong> You shall report to Mr. <strong><u>[Reporting_Manager_Name] ([Reporting_Manager_Designation])</u></strong> for any location / communication please reach up to <strong><u>[Alternative_Contact_Name] ([Alternative_Contact_Phone])</u></strong></p>
  <p>Please sign and return a copy of this letter as acceptance of the terms.</p>
  <p>We welcome you to team [Company_Team_Name]!</p>
  <div class="letter-closing letter-sender-block">
    <p>Thanks,</p>
    <p><strong>[Sender_Name]</strong></p>
    <p>[Sender_Designation]</p>
    <p>Mob. No. [Sender_Mobile]</p>
    <p>[Company_Legal_Name]</p>
    <p>Website: <u>[Company_Website]</u></p>
  </div>
</div>`,
  annexureContent: `ANNEXURE TO THE APPOINTMENT LETTER

The following terms and conditions shall apply to [Candidate_Name] in addition to the appointment letter:

1. Designation: [Job_Title]
2. Department: [Department]
3. Date of Joining: [Joining_Date]
4. Place of Posting: [Office_Location] / [Initial_Reporting_Location]
5. Reporting To: [Reporting_Manager_Name], [Reporting_Manager_Designation]

Salary and other benefits will be as discussed and confirmed by [Company_Management]. For any queries, please contact [Sender_Name] ([Sender_Mobile]) or [Alternative_Contact_Name] ([Alternative_Contact_Phone]).

This annexure forms an integral part of the appointment letter issued by [Company_Legal_Name].`,
};

/** Labels for bracket placeholders. Keys match [Placeholder_Name] in content. */
export const PLACEHOLDER_LABELS = {
  Candidate_Name: "Candidate / Recipient Name",
  Recipient_Name: "Recipient Name",
  Title: "Title (Mr. / Mrs. / Ms.)",
  Parent_Guardian: "Parent/Guardian (e.g. D/O:- ..., S/O:- ...)",
  Address_Line1: "Address Line 1",
  Address_Line2: "Address Line 2",
  Address: "Address",
  Mobile: "Mobile No.",
  Email: "Email",
  Letter_Type: "Letter Type (e.g. Offer Letter)",
  Job_Title: "Job Title / Designation",
  Designation: "Designation",
  Department: "Department",
  Joining_Date: "Joining Date",
  Letter_Date: "Letter Date",
  Date: "Date",
  Initial_Reporting_Location: "Initial Reporting Location",
  Company_Management: "Company/Management Name",
  Office_Location: "Office Location",
  Reporting_Manager_Name: "Reporting Manager Name",
  Reporting_Manager_Designation: "Reporting Manager Designation",
  Alternative_Contact_Name: "Alternative Contact Name",
  Alternative_Contact_Phone: "Alternative Contact Phone",
  Company_Team_Name: "Company / Team Name",
  Sender_Name: "Sender Name (HR)",
  Sender_Designation: "Sender Designation",
  Sender_Mobile: "Sender Mobile No.",
  Company_Legal_Name: "Company Legal Name",
  Company_Name: "Company Name",
  Company_Website: "Company Website",
  Salary: "Salary / CTC (optional)",
  CTC: "CTC",
  Custom_Notes: "Custom Notes (optional)",
};

/** Humanize bracket key to label: Candidate_Name -> "Candidate Name" */
export function getPlaceholderLabel(key) {
  if (!key) return "";
  const label = PLACEHOLDER_LABELS[key];
  if (label) return label;
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Keys that should use DatePicker (any case-insensitive match) */
const DATE_KEYS = ["Joining_Date", "Letter_Date", "Date"];

/** Keys that should use Designation dropdown */
const DESIGNATION_KEYS = ["Job_Title", "Designation"];

/** Keys that should use Department dropdown */
const DEPARTMENT_KEYS = ["Department"];

/** Keys that should use multi-line TextArea */
const MULTILINE_KEYS = [
  "Address_Line1",
  "Address_Line2",
  "Address",
  "Custom_Notes",
];

export function getPlaceholderFieldType(key) {
  const k = key;
  if (DATE_KEYS.some((d) => d.toLowerCase() === k.toLowerCase())) return "date";
  if (DESIGNATION_KEYS.some((d) => d.toLowerCase() === k.toLowerCase()))
    return "designation";
  if (DEPARTMENT_KEYS.some((d) => d.toLowerCase() === k.toLowerCase()))
    return "department";
  if (MULTILINE_KEYS.some((d) => d.toLowerCase() === k.toLowerCase()))
    return "textarea";
  return "text";
}
